import { LogUtils } from "./log-utils";
// Update the import path below to the correct relative or package path where csrfToken utilities are defined
import { getValidCsrfToken, clearCsrfToken } from "./csrfToken";

interface FetchDataRequestOptions {
  method: "GET" | "POST" | "PUT" | "DELETE";
  skipCredentials?: boolean;
  skipCsrf?: boolean;
}

interface FetchDataRequest {
  endpoint: string;
  cache?: RequestCache;
  options?: FetchDataRequestOptions;
  params?: string | Record<string, string> | string[][] | URLSearchParams | undefined;
  body?: unknown;
  headers?: Record<string, string>;
  tags?: string[];
  revalidate?: number;
}

// Helper to build headers
function buildHeaders(customHeaders?: Record<string, string>) {
  return {
    "content-type": "application/json",
    Accept: "application/json",
    ...(customHeaders || {}),
  };
}

// Helper to build endpoint with params
function buildRequestEndpoint(endpoint: string, params?: FetchDataRequest["params"]) {
  if (!params || Object.keys(params).length === 0) return endpoint;
  return decodeURIComponent(`${endpoint}?${new URLSearchParams(params)}`);
}

// Helper to inject CSRF token for POST
async function injectCsrfToken(body: unknown, forceRefresh: boolean) {
  const csrfToken = await getValidCsrfToken(forceRefresh); // getValidCsrfToken always checks sessionStorage
  const newBody: any = body && typeof body === "object" ? { ...body } : {};
  if (csrfToken) newBody._csrf = csrfToken;
  return newBody;
}

interface FetchWithCsrfRetryRequest {
  endpoint: string;
  body: unknown;
  options?: FetchDataRequestOptions;
  headers: Record<string, string>;
  fetchOptions?: Omit<RequestInit, "body" | "method" | "headers">;
}

interface FetchWithCsrfRetryOptions {
  request: FetchWithCsrfRetryRequest;
  cache: RequestCache;
  tags?: string[];
  revalidate?: number;
}

// Helper to perform fetch with retry for POST/CSRF
async function fetchWithCsrfRetry({ request, cache, tags, revalidate }: FetchWithCsrfRetryOptions) {
  let triedRefresh = false;
  let response: Response;
  do {
    const newBody = await injectCsrfToken(request.body, triedRefresh);
    response = await fetch(request.endpoint, {
      method: "POST",
      credentials: "include",
      cache,
      ...(tags && { next: { tags } }),
      ...(revalidate && { next: { revalidate } }),
      body: JSON.stringify(newBody),
      ...(request.fetchOptions || {}),
      ...(request.options || {}),
      headers: request.headers,
    });
    if (
      !response.ok &&
      (response.status === 401 || response.status === 404 || response.status === 403) &&
      !triedRefresh
    ) {
      clearCsrfToken();
      triedRefresh = true;
      continue;
    }
    break;
  } while (true);
  return response;
}

export const fetchData = async (request: FetchDataRequest) => {
  try {
    const {
      endpoint,
      params,
      options,
      body,
      tags,
      cache = "force-cache",
      revalidate,
      headers: customHeaders,
    } = request;

    const methodType = options?.method ?? "GET";
    const skipCsrf = options?.skipCsrf ?? false;
    const skipCredentials = options?.skipCredentials || "include";
    const hasRequestBody = body ? Object.keys(body).length > 0 : false;
    const requestEndpoint = buildRequestEndpoint(endpoint, params);
    const headers = buildHeaders(customHeaders);

    let response: Response;
    if (methodType.toUpperCase() === "POST" && !skipCsrf) {
      response = await fetchWithCsrfRetry({
        request: {
          endpoint: requestEndpoint,
          body,
          options,
          headers,
        },
        cache,
        tags,
        revalidate,
      });
    } else {
      response = await fetch(requestEndpoint, {
        method: methodType,
        ...(!skipCredentials ? { credentials: "include" } : {}),
        cache,
        ...(tags && { next: { tags } }),
        ...(revalidate && { next: { revalidate } }),
        ...(methodType !== "GET" && hasRequestBody && { body: JSON.stringify(body) }),
        ...options,
        headers,
      });
    }
    // Check the response Content-Type to determine how to parse it
    const contentType = response?.headers?.get("content-type");
    let data;
    if (contentType?.includes("application/json")) {
      data = await response.json();
    } else {
      // Handle as text if not JSON
      data = await response.text();
    }
    if (response.ok) {
      return data;
    } else {
      return null;
    }
  } catch (error: unknown) {
    LogUtils.error(`Error fetching data from : ${request.endpoint}`, error);
  }
};
